import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ShieldCheck, Users, Vote, Plus, BadgeCheck, Clock, AlertTriangle, KeyRound, Search, Copy, Check, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import VerificationGate from "@/components/VerificationGate";
import OrgTeamPanel from "@/components/OrgTeamPanel";

/** Trade Organization dashboard
 *  Two columns: Committee (governance) · Member Directory.
 *  Plus a "Pending Proposals" inbox surfacing the 3-of-N OTP guardrail. */
export default function TradeOrgDashboard() {
  const { user } = useAuth();
  const [org, setOrg]         = useState(null);
  const [members, setMembers] = useState([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [proposals, setProposals] = useState([]);
  const [onJob, setOnJob] = useState({ count: 0, members: [], by_dept: {} });
  const [search, setSearch] = useState("");
  const [proposeOpen, setProposeOpen] = useState(false);
  const [approveProp, setApproveProp] = useState(null);
  const [icardOpen, setIcardOpen] = useState(false);
  const [issuedLink, setIssuedLink] = useState("");

  const refresh = async () => {
    const [{ data: orgResp }] = await Promise.all([
      api.get("/onboard/me/organization"),
    ]);
    if (!orgResp.organization) return;
    setOrg(orgResp.organization);
    const oid = orgResp.organization.id;
    const [memb, props, job] = await Promise.all([
      api.get(`/orgs/${oid}/members`, { params: { q: search, limit: 50 } }).catch(() => ({ data: { items: [], total: 0 } })),
      api.get(`/orgs/${oid}/committee/proposals`).catch(() => ({ data: { items: [] } })),
      api.get(`/orgs/${oid}/on-the-job`).catch(() => ({ data: { count: 0, members: [], by_dept: {} } })),
    ]);
    setMembers(memb.data.items || []);
    setMemberTotal(memb.data.total || 0);
    setProposals(props.data.items || []);
    setOnJob(job.data);
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!org) return;
    const t = setTimeout(() => refresh(), 250);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (!org) {
    return <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55">Loading dashboard…</div>;
  }
  if (org.type !== "association") {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="cs-card rounded-2xl p-10 text-center">
          <ShieldCheck size={32} className="mx-auto text-white/40 mb-4" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Trade Org Only</h1>
          <p className="mt-3 text-sm text-white/65">This dashboard is for Trade Organizations. Your org is registered as &ldquo;{org.type}&rdquo;.</p>
        </div>
      </div>
    );
  }

  // ── Verification Gate ────────────────────────────────────────────────
  // Until the Trade Org has uploaded Certificate + Reg Number + Logo, the
  // entire dashboard is locked. Other org types skip this gate.
  if (!org.verified) {
    return (
      <VerificationGate
        org={org}
        onVerified={(updated) => setOrg(updated)}
      />
    );
  }

  const committee = org.committee_members || [];
  const termEnds = org.committee_term_ends_at;
  const termExpired = termEnds && new Date(termEnds) < new Date();
  const pending = proposals.filter((p) => p.status === "pending");

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="trade-org-dashboard">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Trade Organization</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
        {org.brand_name}
      </h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        <span>{org.legal_name}</span>
        <span className="text-white/25">·</span>
        <span className="capitalize">{org.trade_org_industry || "industry not set"}</span>
        {org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
            <BadgeCheck size={10} className="inline mr-1" /> Verified
          </span>
        )}
      </div>

      {/* Term status banner */}
      <div className={`mt-6 cs-card rounded-2xl p-4 flex items-center gap-3 ${termExpired ? "border border-red-500/40" : ""}`} data-testid="term-banner">
        <Clock size={18} className={termExpired ? "text-red-300" : "text-white/55"} />
        <div className="flex-1">
          <div className="text-xs text-white/55 tracking-[0.18em] uppercase">Committee Term</div>
          <div className="text-sm mt-0.5">
            {termEnds ? (
              termExpired ? <span className="text-red-300 font-semibold">Expired — fresh election required</span>
                          : <>Ends {new Date(termEnds).toLocaleDateString()}{" "}
                              <span className="text-white/40">· {org.committee_term_years} year term</span></>
            ) : <span className="text-white/40">No term set</span>}
          </div>
        </div>
        <Dialog open={icardOpen} onOpenChange={(v) => { setIcardOpen(v); if (!v) setIssuedLink(""); }}>
          <DialogTrigger asChild>
            <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
              data-testid="issue-icard-btn">
              <Plus size={12} className="mr-1.5" /> Issue iCard
            </Button>
          </DialogTrigger>
          <IssueIcardDialog
            orgId={org.id}
            issuedLink={issuedLink}
            setIssuedLink={setIssuedLink}
            onOpenChange={setIcardOpen}
            onDone={refresh}
          />
        </Dialog>
        <Link to="/icard/mockups"
              data-testid="preview-mockups-link"
              className="h-9 inline-flex items-center px-3 rounded-full text-[10px] uppercase tracking-[0.22em] border border-white/15 text-white/75 hover:border-white/35 hover:text-white">
          <Sparkles size={12} className="mr-1.5 text-[#F5B82E]" /> Preview iCard styles
        </Link>
      </div>

      {/* On The Job Today */}
      <div className="mt-4 cs-card rounded-2xl p-5" data-testid="on-the-job-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">On The Job Today</div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="font-display text-4xl" style={{ color: "#10B981" }}>{onJob.count}</span>
              <span className="text-sm text-white/55">of {onJob.members_total || memberTotal} members working</span>
            </div>
          </div>
          {Object.keys(onJob.by_dept || {}).length > 0 && (
            <div className="flex flex-wrap gap-2 max-w-md justify-end">
              {Object.entries(onJob.by_dept).map(([d, n]) => (
                <span key={d} className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                  {d} · {n}
                </span>
              ))}
            </div>
          )}
        </div>
        {onJob.count > 0 && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="on-the-job-list">
            {onJob.members.slice(0, 9).map((m) => (
              <div key={m.user_id} className="flex items-center gap-2.5 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/15"
                data-testid={`on-the-job-${m.user_id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {m.avatar_url ? (
                  <img src={m.avatar_url} className="w-7 h-7 rounded-full" alt="" />
                ) : <div className="w-7 h-7 rounded-full bg-white/10" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white truncate">{m.name}</div>
                  <div className="text-[10px] text-white/55 truncate">{m.role_label} · {m.project_title}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Committee + Pending proposals (left, 1 col) */}
        <div className="space-y-6">
          <div className="cs-card rounded-2xl p-6" data-testid="committee-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Committee</div>
                <h2 className="font-display text-xl uppercase tracking-tight">
                  {committee.length} / 11 seated
                </h2>
              </div>
              <Dialog open={proposeOpen} onOpenChange={setProposeOpen}>
                <DialogTrigger asChild>
                  <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/10 hover:bg-white/15 text-white"
                    data-testid="propose-committee-btn">
                    <Plus size={12} className="mr-1.5" /> Propose
                  </Button>
                </DialogTrigger>
                <ProposeDialog onOpenChange={setProposeOpen} orgId={org.id} onCreated={refresh} />
              </Dialog>
            </div>
            {committee.length === 0 ? (
              <div className="text-sm text-white/55 py-3">
                No committee members yet. Use &ldquo;Propose&rdquo; to add a Secretary, Treasurer, or other member. Three OTP approvals will be required.
              </div>
            ) : (
              <div className="space-y-2.5" data-testid="committee-list">
                {committee.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/10">
                    <div className="w-9 h-9 rounded-full bg-[#6B4EFF]/25 text-[#6B4EFF] flex items-center justify-center text-xs font-bold">
                      {m.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{m.name}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-white/55">{m.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="cs-card rounded-2xl p-6" data-testid="proposals-card">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Pending Proposals</div>
            <h2 className="font-display text-xl uppercase tracking-tight mt-1">
              {pending.length} awaiting approvals
            </h2>
            {pending.length === 0 ? (
              <div className="mt-3 text-sm text-white/55">All quiet. Proposals show up here when someone wants to add or remove a committee member.</div>
            ) : (
              <div className="mt-4 space-y-2.5" data-testid="proposals-list">
                {pending.map((p) => (
                  <div key={p.id} className="p-3 rounded-lg bg-amber-500/8 border border-amber-500/25"
                    data-testid={`proposal-${p.id}`}>
                    <div className="flex items-center gap-2">
                      <Vote size={12} className="text-amber-300" />
                      <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300">{p.action}</span>
                      <span className="text-white/40 text-[10px]">·</span>
                      <span className="text-xs text-white/85 font-semibold">{p.member_name}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-white/55">
                      {p.approvals?.length || 0} / {p.required_approvals} approvals · proposed by {p.proposer_name}
                    </div>
                    <Button onClick={() => setApproveProp(p)}
                      className="mt-3 h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] bg-amber-500/25 hover:bg-amber-500/35 text-amber-200"
                      data-testid={`approve-btn-${p.id}`}>
                      <KeyRound size={11} className="mr-1.5" /> Submit OTP
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Member directory (right, 2 cols) */}
        <div className="lg:col-span-2 cs-card rounded-2xl p-6" data-testid="members-card">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Member Directory</div>
              <h2 className="font-display text-xl uppercase tracking-tight">
                {memberTotal.toLocaleString()} registered
              </h2>
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name / phone / membership #"
                className="h-10 w-72 bg-black/40 border-white/15 pl-9"
                data-testid="member-search" />
            </div>
          </div>

          {members.length === 0 ? (
            <div className="text-sm text-white/55 py-8 text-center">
              No members yet for <span className="font-mono">{org.brand_name?.toLowerCase()}</span>. Members
              join after you issue iCards from your roster — coming in the next slice.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-white/10" data-testid="members-table">
              <table className="w-full text-left">
                <thead className="bg-white/5">
                  <tr className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                    <th className="px-3 py-2.5">Member</th>
                    <th className="px-3 py-2.5">Role</th>
                    <th className="px-3 py-2.5">Membership #</th>
                    <th className="px-3 py-2.5">City</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`member-row-${m.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2.5">
                          {m.on_the_job_today && (
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0"
                              title="On the job today" />
                          )}
                          {m.avatar_url ? (
                            <img src={m.avatar_url} alt="" className="w-7 h-7 rounded-full border border-white/15" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-white/10" />
                          )}
                          <span className="text-sm text-white">{m.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] text-white/70">{m.primary_role}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono text-white/55">{m.membership_number || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-white/55">{m.city || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {memberTotal > members.length && (
                <div className="px-3 py-2 text-[11px] text-white/45 bg-white/[0.03] border-t border-white/5">
                  Showing {members.length} of {memberTotal} · refine search to narrow
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Team / Back-office staff */}
      <div className="mt-6">
        <OrgTeamPanel org={org} />
      </div>

      {approveProp && (
        <ApproveDialog
          prop={approveProp}
          orgId={org.id}
          onOpenChange={() => setApproveProp(null)}
          onApproved={refresh}
        />
      )}
    </div>
  );
}

function ProposeDialog({ onOpenChange, orgId, onCreated }) {
  const [action, setAction] = useState("add");
  const [memberName, setMemberName] = useState("");
  const [memberRole, setMemberRole] = useState("member");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberPhone, setMemberPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${orgId}/committee/propose`, {
        action, member_name: memberName, member_role: memberRole,
        member_email: memberEmail || null, member_phone: memberPhone,
      });
      toast.success("Proposal created — OTPs sent to approvers");
      // Surface own OTP via dev peek
      try {
        const me = await api.get(`/orgs/${orgId}/committee/proposals/${data.id}/otp-for-dev`);
        if (me.data?.otp) {
          toast.info(`Your OTP (dev): ${me.data.otp}`, { duration: 12000 });
        }
      } catch (err) { console.warn("[TradeOrgDashboard] approver-only path skipped", err); }
      onOpenChange(false);
      await onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not propose");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="propose-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Propose Committee Change</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          3 OTP approvals required. Approvers get the code via email + SMS.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Action</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button onClick={() => setAction("add")}
              className={`p-2.5 rounded-lg border text-sm uppercase tracking-[0.18em] ${action === "add" ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "bg-black/30 border-white/10 text-white/65"}`}
              data-testid="action-add">Add</button>
            <button onClick={() => setAction("remove")}
              className={`p-2.5 rounded-lg border text-sm uppercase tracking-[0.18em] ${action === "remove" ? "bg-red-500/15 border-red-500/50 text-red-300" : "bg-black/30 border-white/10 text-white/65"}`}
              data-testid="action-remove">Remove</button>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Member Name *</Label>
          <Input value={memberName} onChange={(e) => setMemberName(e.target.value)}
            className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Role</Label>
            <Input value={memberRole} onChange={(e) => setMemberRole(e.target.value)}
              placeholder="secretary / treasurer / member"
              className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-role" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Phone</Label>
            <Input value={memberPhone} onChange={(e) => setMemberPhone(e.target.value)}
              placeholder="+91 …"
              className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-phone" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Email</Label>
          <Input type="email" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)}
            className="mt-2 h-10 bg-black/40 border-white/15" data-testid="propose-email" />
        </div>
        <Button onClick={submit} disabled={!memberName || busy}
          className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
          data-testid="propose-submit">
          {busy ? "Sending OTPs…" : "Submit Proposal"}
        </Button>
      </div>
    </DialogContent>
  );
}

function ApproveDialog({ prop, orgId, onOpenChange, onApproved }) {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  // Auto-fetch this user's OTP from the dev-peek endpoint
  useEffect(() => {
    api.get(`/orgs/${orgId}/committee/proposals/${prop.id}/otp-for-dev`)
       .then(({ data }) => setOtp(data.otp || ""))
       .catch((err) => console.warn("[TradeOrg] dev-otp fetch failed:", err?.message));
  }, [prop.id, orgId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/orgs/${orgId}/committee/proposals/${prop.id}/approve`,
        null, { params: { otp } }
      );
      if (data.status === "approved") {
        toast.success("Threshold met — committee change applied");
      } else {
        toast.success(`Approval recorded (${data.approvals}/${data.required})`);
      }
      onOpenChange();
      await onApproved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not approve");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Submit OTP Approval</DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {prop.action.toUpperCase()} · {prop.member_name} · {prop.approvals?.length || 0}/{prop.required_approvals} approvals
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white/5 border border-white/10">
            <AlertTriangle size={14} className="text-amber-300 shrink-0 mt-0.5" />
            <div className="text-[11px] text-white/65">
              <strong>Dev mode</strong>: your OTP is auto-filled below. In production it arrives via email + SMS.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">6-digit OTP</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6} placeholder="000000"
              className="mt-2 h-12 bg-black/40 border-white/15 font-mono text-2xl tracking-[0.4em] text-center"
              data-testid="approve-otp-input" autoFocus />
          </div>
          <Button onClick={submit} disabled={otp.length !== 6 || busy}
            className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="approve-submit">
            {busy ? "Verifying…" : "Approve"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IssueIcardDialog({ orgId, issuedLink, setIssuedLink, onOpenChange, onDone }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [memNo, setMemNo] = useState("");
  const [role, setRole] = useState("production");
  const [city, setCity] = useState("Mumbai");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${orgId}/icard-invites`, {
        invitee_name: name, invitee_phone: phone, membership_number: memNo,
        primary_role: role, city,
      });
      const url = data.invite_url?.startsWith("http")
        ? data.invite_url : `${window.location.origin}${data.invite_url}`;
      setIssuedLink(url);
      toast.success("iCard issued — share the link with the member");
      await onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not issue");
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(issuedLink).then(() => {
      setCopied(true); toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (issuedLink) {
    return (
      <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="icard-issued-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">iCard Ready</DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Share this link with your member. They open it on their phone, set a password and the iCard lands fully populated.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-[11px] font-mono text-white/70 break-all"
            data-testid="icard-issued-link">{issuedLink}</div>
          <Button onClick={copy} className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="copy-icard-link">
            {copied ? <Check size={13} className="mr-2" /> : <Copy size={13} className="mr-2" />}
            {copied ? "Copied" : "Copy Link"}
          </Button>
          <Button onClick={() => { setIssuedLink(""); onOpenChange(false); }}
            className="w-full h-10 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/10 hover:bg-white/15 text-white">
            Done
          </Button>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="issue-icard-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Issue Member iCard</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          Generate a claim link. Sent via SMS/email in v2; copy-paste for now.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Full Name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Phone *</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 …"
              className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-phone-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Membership # *</Label>
            <Input value={memNo} onChange={(e) => setMemNo(e.target.value)}
              placeholder="IFTDA/D/2024/00501"
              className="mt-2 h-10 bg-black/40 border-white/15 font-mono uppercase" data-testid="icard-memno-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Primary Role</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)}
              placeholder="director / dop / editor / production"
              className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-role-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)}
              className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-city-input" />
          </div>
        </div>
        <Button onClick={submit} disabled={!name || !phone || !memNo || busy}
          className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
          data-testid="icard-submit">
          {busy ? "Issuing…" : "Issue iCard"}
        </Button>
      </div>
    </DialogContent>
  );
}

